import { type FC, useCallback, useEffect, useState } from 'react'

import { InlineWidget } from 'react-calendly'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'

import { useAccount } from '../accounts/hooks/useAccounts'
import { type CalendlyEventType, CalendlyEvents } from './types'
import { isCalendlyEvent } from './utils'

interface Props {
  isOpen: boolean
  handleClose: () => void
}

export const OnBoardingCallPopup: FC<Props> = ({ isOpen, handleClose }) => {
  const [showCalendly, setShowCalendly] = useState(false)
  const { updateOwner, owner } = useAccount()

  const handleDialogClose = useCallback(() => {
    handleClose()
    if (owner?.openOnboardingPopup) {
      updateOwner({ openOnboardingPopup: false })
    }
  }, [handleClose, owner, updateOwner])

  const handleBookCall = () => {
    setShowCalendly(true)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEventBooked = (event: MessageEvent<unknown>) => {
    if (!isCalendlyEvent) return
    const eventType = event.data as CalendlyEventType

    if (eventType.event !== CalendlyEvents.SCHEDULES_EVENT) return
    handleDialogClose()
    updateOwner({
      bookedOnboardingCall: true,
      showOnboardingButton: false,
      openOnboardingPopup: false,
    })
  }

  useEffect(() => {
    window.addEventListener('message', handleEventBooked)

    return () => window.removeEventListener('message', handleEventBooked)
    // Should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEventBooked])

  return (
    <Dialog
      show={isOpen}
      bigDialog={false}
      onClose={handleDialogClose}
    >
      <Dialog.Header>
        <Dialog.Title>Onboarding Call</Dialog.Title>
        <Dialog.Description className="text-center">
          Book a quick onboarding call to discover all of our features
        </Dialog.Description>
      </Dialog.Header>

      {showCalendly ? (
        <div className="mt-8">
          <InlineWidget url="https://calendly.com/creatorhero-meeting/onboarding" />
        </div>
      ) : (
        <>
          <div className="text-text pt-8 text-sm font-normal">
            <p>
              Due to the amount of features and tools we provide you with, our
              software might be a bit overwhelming in the beginning.
            </p>
            <p className="mt-5">
              That’s why we want to offer you a{' '}
              <span className="font-medium underline">
                quick onboarding call
              </span>{' '}
              with a member of our team, so you can make the most money out of
              this. The call will take approximately{' '}
              <span className="font-medium underline">10-20 minutes</span>.
            </p>
          </div>

          <span className="text-text mt-8 block font-medium">
            To schedule a call, click the button below
          </span>

          <Dialog.Footer>
            <Button
              className="w-full"
              onClick={handleBookCall}
            >
              Book Call
            </Button>
          </Dialog.Footer>
        </>
      )}
    </Dialog>
  )
}
